// SchoolPeriodUserMapper.ts
import {
  SchoolPeriodUser,
  SchoolPeriodUserInput
} from '@/core/domain/school/user/SchoolPeriodUser.ts'
import { UserRole } from '@/core/domain/resources/enums/UserRole.ts'
import {
  type SchoolPeriodUserEntity,
  SchoolPeriodUserInputSchema
} from '@/core/infrastructure/schemas/school/user/SchoolPeriodUserSchema.ts'

export default class SchoolPeriodUserMapper {
  /**
   * Valide par les contraintes de Zod et retourne les erreurs.
   * @param input
   */
  static validateSchoolPeriodUserInput(input: SchoolPeriodUserInput): Map<string, string[]> {
    const parsed = SchoolPeriodUserInputSchema.safeParse(input)
    if (!parsed.success) return new Map(Object.entries(parsed.error.flatten().fieldErrors))
    return new Map()
  }

  /**
   * Convertit les données d'un utilisateur et d'un SchoolPeriodUser en SchoolPeriodUserInput
   * @param schoolPeriodUser Données de l'utilisateur dans le contexte d'une période scolaire
   * @param user Données complètes de l'utilisateur
   * @param schoolPeriodId Identifiant de la période scolaire
   * @returns Un objet SchoolPeriodUserInput prêt pour le formulaire
   */
  static toInput(
    schoolPeriodUser: SchoolPeriodUser,
    user: SchoolPeriodUser,
    schoolPeriodId: number
  ): SchoolPeriodUserInput {
    const input = new SchoolPeriodUserInput()

    function convertRole(role: UserRole.Values): UserRole.SchoolValues {
      switch (role) {
        case UserRole.Values.PERDIR:
          return UserRole.SchoolValues.PERDIR
        case UserRole.Values.TEACHER:
          return UserRole.SchoolValues.TEACHER
        case UserRole.Values.STUDENT:
          return UserRole.SchoolValues.STUDENT
        case UserRole.Values.CPE:
          return UserRole.SchoolValues.CPE
        default:
          throw new Error(`Role ${role} is not a school role`)
      }
    }

    // Données de base de SchoolPeriodUser
    input.identifier = schoolPeriodUser.userIdentifier ?? undefined
    input.firstname = schoolPeriodUser.firstname
    input.lastname = schoolPeriodUser.lastname

    // Données supplémentaires de User
    input.email = user.email
    input.gender = user.gender
    input.birthdate = user.birthdate
    input.schoolPeriodId = schoolPeriodId

    // Conversion des rôles
    input.roles = schoolPeriodUser.role.map(convertRole)

    // Ajout de la propriété hasSignedAgreement pour les étudiants
    input.hasSignedAgreement =
      input.role === UserRole.SchoolValues.STUDENT ? schoolPeriodUser.hasSignedAgreement : undefined

    return input
  }

  /**
   * Convertit un SchoolPeriodUser en SchoolPeriodUserEntity
   * @param user
   */
  static toEntity(user: SchoolPeriodUser): SchoolPeriodUserEntity {
    return {
      schoolPeriodUserId: user.schoolPeriodUserId,
      user: {
        identifier: user.userIdentifier,
        firstname: user.firstname,
        lastname: user.lastname,
        username: user.username,
        roles: user.role,
        email: user.email,
        gender: user.gender,
        birthdate: user.birthdate,
        createdAt: user.createdAt,
        modifiedAt: user.modifiedAt
      },
      hasSignedAgreement: user.hasSignedAgreement
    } satisfies SchoolPeriodUserEntity
  }
}
